import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Inbox, Sparkles, Check, X, ExternalLink, RefreshCw, Loader2, Zap, Info,
  Target, AlertTriangle, RotateCcw, MessageSquare,
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OBJECT_TYPE_META = {
  decision_tree: { label: 'Decision Tree', color: '#5856D6' },
  faq: { label: 'FAQ', color: '#007AFF' },
  checklist: { label: 'Checklist', color: '#34C759' },
  pattern_card: { label: 'Pattern Card', color: '#FF9500' },
  anti_pattern: { label: 'Anti-pattern', color: '#FF3B30' },
  runbook: { label: 'Runbook', color: '#8B5CF6' },
};

export default function DiffusionInboxCard({ headers }) {
  const [diffusions, setDiffusions] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [whyDraft, setWhyDraft] = useState(null);
  const [tryDraft, setTryDraft] = useState(null);       // { diffusion, challenge }
  const [outcomeNote, setOutcomeNote] = useState('');
  const [challengeLoading, setChallengeLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cop/diffusions/mine?limit=20`, { headers });
      setDiffusions(r.data.diffusions || []);
      setUnread(r.data.unread || 0);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const mark = async (d, status) => {
    setBusyId(d.diffusion_id);
    try {
      await axios.post(
        `${API}/cop/diffusions/${d.diffusion_id}/mark?status=${status}`,
        {}, { headers },
      );
      setDiffusions(prev => prev.map(x =>
        x.diffusion_id === d.diffusion_id ? { ...x, status } : x,
      ));
      if (status === 'dismissed') {
        setDiffusions(prev => prev.filter(x => x.diffusion_id !== d.diffusion_id));
      }
      if (d.status === 'new') setUnread(u => Math.max(0, u - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };

  const openArticle = (d) => {
    mark(d, 'read');
    if (d.article_id) {
      window.open(`/articles?article=${d.article_id}`, '_blank');
    } else {
      window.open(`/cop`, '_blank');
    }
  };

  const tryIt = async (d) => {
    if (!d.article_id) return;
    setTryDraft({ diffusion: d, challenge: null });
    setChallengeLoading(true);
    setOutcomeNote('');
    try {
      const r = await axios.post(
        `${API}/cop/assets/${d.article_id}/challenge`, {}, { headers },
      );
      setTryDraft({ diffusion: d, challenge: r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Challenge failed to generate');
      setTryDraft(null);
    } finally { setChallengeLoading(false); }
  };

  const submitOutcome = async (outcome) => {
    if (!tryDraft?.diffusion?.article_id) return;
    setBusyId(tryDraft.diffusion.diffusion_id);
    try {
      const r = await axios.post(
        `${API}/cop/assets/${tryDraft.diffusion.article_id}/apply`,
        { outcome, note: outcomeNote.trim() || undefined },
        { headers },
      );
      const variantMsg = r.data.variant_crystallised
        ? ' · 🔬 Variant crystallised from modifications!' : '';
      toast.success(
        `+${r.data.tokens_awarded} contribution tokens · balance ${r.data.new_balance}${variantMsg}`,
        { duration: 5000 },
      );
      setDiffusions(prev => prev.map(x =>
        x.diffusion_id === tryDraft.diffusion.diffusion_id
          ? { ...x, status: 'read', applied_outcome: outcome }
          : x,
      ));
      setTryDraft(null);
      setOutcomeNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Outcome log failed');
    } finally { setBusyId(null); }
  };

  if (!loading && diffusions.length === 0) return null;

  return (
    <Card className="border-[#002FA7]/30 bg-[#002FA7]/3" data-testid="diffusion-inbox-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Inbox className="w-4 h-4 text-[#002FA7]" />
            This week in your corner
            {unread > 0 && (
              <Badge className="text-[9px] bg-[#FF3B30] text-white border-0" data-testid="diffusion-unread-badge">
                {unread} new
              </Badge>
            )}
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="diffusion-refresh">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-[11px] text-[#4B5563] mb-3">
          Knowledge assets the CoP Vitality Loop routed to you based on your role, open tasks, and recent signals.
        </p>
        {loading && diffusions.length === 0 ? (
          <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#9CA3AF]" /></div>
        ) : (
          <div className="space-y-2">
            {diffusions.map(d => {
              const meta = OBJECT_TYPE_META[d.object_type] || { label: 'Asset', color: '#4B5563' };
              const isNew = d.status === 'new';
              const isPush = d.channel === 'push';
              const scorePct = Math.round((d.score || 0) * 100);
              return (
                <div
                  key={d.diffusion_id}
                  className={`bg-white border rounded-lg p-3 transition-all ${
                    isNew ? 'border-[#002FA7]/30' : 'border-[#E5E7EB] opacity-80'
                  }`}
                  data-testid={`diffusion-${d.diffusion_id}`}
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <h4 className="text-sm font-semibold text-[#0A0A0A]">{d.title}</h4>
                        <Badge
                          className="text-[9px] border-0"
                          style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                        >
                          {meta.label}
                        </Badge>
                        {isPush && (
                          <Badge className="text-[9px] bg-[#5856D6]/10 text-[#5856D6] border-0" data-testid={`channel-push-${d.diffusion_id}`}>
                            <Zap className="w-2 h-2 mr-0.5" />pushed
                          </Badge>
                        )}
                        {isNew && (
                          <Badge className="text-[9px] bg-[#FF3B30]/10 text-[#FF3B30] border-0">
                            new
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-[#4B5563] line-clamp-2">{d.summary}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-[#9CA3AF]">
                        <span>Relevance {scorePct}%</span>
                        <button
                          type="button"
                          onClick={() => setWhyDraft(d)}
                          className="inline-flex items-center gap-0.5 text-[#002FA7] hover:underline"
                          data-testid={`diffusion-why-${d.diffusion_id}`}
                          title="Show me why I got this"
                        >
                          <Info className="w-3 h-3" />why?
                        </button>
                        {(d.reasons || []).slice(0, 2).map((r, i) => (
                          <span key={i}>· {r}</span>
                        ))}
                        <span>· {format(new Date(d.created_at), 'MMM d, HH:mm')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => openArticle(d)}
                      className="h-7 text-[11px] bg-[#002FA7] hover:bg-[#00227A] text-white"
                      data-testid={`diffusion-open-${d.diffusion_id}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />Open
                    </Button>
                    {!d.applied_outcome && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => tryIt(d)}
                        disabled={busyId === d.diffusion_id}
                        className="h-7 text-[11px] border-[#5856D6]/30 text-[#5856D6] hover:bg-[#5856D6]/5"
                        data-testid={`diffusion-try-${d.diffusion_id}`}
                      >
                        <Target className="w-3 h-3 mr-1" />Try it
                      </Button>
                    )}
                    {d.applied_outcome && (
                      <Badge className="text-[9px] bg-[#34C759]/10 text-[#34C759] border-0" data-testid={`applied-${d.diffusion_id}`}>
                        <Check className="w-2.5 h-2.5 mr-0.5" />applied ({d.applied_outcome.replace(/_/g, ' ')})
                      </Badge>
                    )}
                    {isNew && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => mark(d, 'read')}
                        disabled={busyId === d.diffusion_id}
                        className="h-7 text-[11px] text-[#4B5563]"
                        data-testid={`diffusion-mark-read-${d.diffusion_id}`}
                      >
                        <Check className="w-3 h-3 mr-1" />Mark read
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => mark(d, 'dismissed')}
                      disabled={busyId === d.diffusion_id}
                      className="h-7 text-[11px] text-[#9CA3AF] hover:text-[#FF3B30]"
                      data-testid={`diffusion-dismiss-${d.diffusion_id}`}
                    >
                      <X className="w-3 h-3 mr-1" />Dismiss
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Explainability modal */}
      <Dialog open={!!whyDraft} onOpenChange={(o) => !o && setWhyDraft(null)}>
        <DialogContent className="max-w-md" data-testid="why-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-4 h-4 text-[#002FA7]" />Why this was routed to you
            </DialogTitle>
          </DialogHeader>
          {whyDraft && (
            <div className="space-y-3">
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-3">
                <p className="text-xs font-semibold text-[#0A0A0A]">{whyDraft.title}</p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                  Channel: <strong className="text-[#4B5563]">{whyDraft.channel}</strong>
                  {' · '}
                  Final score: <strong className="text-[#0A0A0A]">{Math.round((whyDraft.score || 0) * 100)}%</strong>
                </p>
              </div>
              {whyDraft.breakdown ? (
                <div className="space-y-2">
                  {[
                    { key: 'role_affinity', name: 'Role affinity', color: '#5856D6' },
                    { key: 'topic_history', name: 'Topic history', color: '#007AFF' },
                    { key: 'open_questions', name: 'Open tasks', color: '#FF9500' },
                    { key: 'adjacency', name: 'Adjacency', color: '#34C759' },
                  ].map(row => {
                    const b = whyDraft.breakdown[row.key] || {};
                    const rawPct = Math.round((b.raw || 0) * 100);
                    const contrib = b.contribution || 0;
                    return (
                      <div
                        key={row.key}
                        className="bg-white border border-[#E5E7EB] rounded-md p-2"
                        data-testid={`breakdown-${row.key}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-[11px] font-medium text-[#0A0A0A]">
                            {row.name}
                          </span>
                          <span className="text-[10px] text-[#4B5563]">
                            <strong style={{ color: row.color }}>{rawPct}%</strong>
                            <span className="text-[#9CA3AF]"> × {(b.weight || 0).toFixed(1)} = </span>
                            <strong className="text-[#0A0A0A]">{(contrib * 100).toFixed(1)}%</strong>
                          </span>
                        </div>
                        <div className="w-full h-1 bg-[#F3F4F6] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${rawPct}%`, backgroundColor: row.color }}
                          />
                        </div>
                        <p className="text-[10px] text-[#9CA3AF] mt-1">{b.label}</p>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between pt-2 border-t border-[#F3F4F6] text-[11px]">
                    <span className="text-[#4B5563] font-semibold">Total</span>
                    <span className="text-[#002FA7] font-bold">{Math.round((whyDraft.score || 0) * 100)}%</span>
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] italic">
                    Formula: role × 0.4 + topic_history × 0.3 + open_tasks × 0.2 + adjacency × 0.1
                    {(whyDraft.score || 0) >= 0.55 && <><br />Push threshold (55%) met → you received a Web Push (or it was deferred to digest if you'd already hit your 3-per-week cap).</>}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-[#9CA3AF]">No breakdown available for this diffusion.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stage 5 — Deliberate Practice dialog */}
      <Dialog open={!!tryDraft} onOpenChange={(o) => !o && setTryDraft(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="try-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#5856D6]" />Try it — deliberate practice
            </DialogTitle>
          </DialogHeader>
          {tryDraft && (
            <div className="space-y-3">
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-3">
                <p className="text-xs font-semibold text-[#0A0A0A]">{tryDraft.diffusion.title}</p>
                <p className="text-[11px] text-[#4B5563] mt-0.5 line-clamp-2">{tryDraft.diffusion.summary}</p>
              </div>

              {challengeLoading && (
                <div className="text-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#9CA3AF]" />
                  <p className="text-[11px] text-[#9CA3AF] mt-2">GPT-5.2 tailoring a scenario to your role…</p>
                </div>
              )}

              {tryDraft.challenge && !challengeLoading && (
                <>
                  <div className="bg-[#5856D6]/5 border-l-3 border-[#5856D6] rounded-md p-3" data-testid="challenge-scenario">
                    <p className="text-[10px] uppercase tracking-wider text-[#5856D6] font-semibold mb-1.5">
                      Scenario
                    </p>
                    <pre className="whitespace-pre-wrap text-[11px] text-[#0A0A0A] leading-relaxed font-sans">
                      {tryDraft.challenge.scenario_md}
                    </pre>
                  </div>

                  {(tryDraft.challenge.key_questions || []).length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                        Key questions
                      </p>
                      <ul className="space-y-1">
                        {tryDraft.challenge.key_questions.map((q, i) => (
                          <li key={i} className="text-[11px] text-[#4B5563] flex items-start gap-1.5" data-testid={`key-q-${i}`}>
                            <span className="text-[#5856D6] font-bold">·</span>{q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-medium flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />Optional 1-2 sentence note
                </label>
                <Textarea
                  value={outcomeNote}
                  onChange={e => setOutcomeNote(e.target.value)}
                  placeholder="What did you do? Anything you'd adapt for your context?"
                  rows={2}
                  maxLength={800}
                  className="mt-1 text-xs"
                  data-testid="outcome-note-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 pt-1" data-testid="outcome-grid">
                {[
                  { key: 'used_as_is', label: 'Used as-is', icon: Check, color: '#34C759', desc: '+3 tokens' },
                  { key: 'used_with_modification', label: 'Used with modification', icon: Sparkles, color: '#FF9500', desc: '+5 tokens' },
                  { key: 'hit_issue', label: 'Tried & hit issue', icon: AlertTriangle, color: '#FF3B30', desc: '+2 tokens' },
                  { key: 'not_applicable', label: 'Not applicable here', icon: RotateCcw, color: '#9CA3AF', desc: '+1 token' },
                ].map(o => {
                  const Ico = o.icon;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => submitOutcome(o.key)}
                      disabled={!!busyId}
                      data-testid={`outcome-${o.key}`}
                      className="inline-flex flex-col items-start gap-0.5 p-2 rounded-md border text-left transition-colors hover:shadow-sm"
                      style={{ borderColor: `${o.color}30`, backgroundColor: `${o.color}08` }}
                    >
                      <div className="flex items-center gap-1.5">
                        <Ico className="w-3.5 h-3.5" style={{ color: o.color }} />
                        <span className="text-[11px] font-semibold text-[#0A0A0A]">{o.label}</span>
                      </div>
                      <span className="text-[10px]" style={{ color: o.color }}>{o.desc}</span>
                    </button>
                  );
                })}
              </div>

              <p className="text-[10px] text-[#9CA3AF] italic pt-1">
                Your outcome feeds Stage 6 reflection — modifications cluster into variant signals that can fork new assets.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
